import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { ButtonVariant, StyledButton } from './Button.styles';
import { getUrlWithTrailingSlash, isExternalUrl } from '@utils/urlHelpers.util';
import { trackImpactRadius } from '@utils/impactRadiusUtils';
const { segment } = require('../../../static/js/joy');

interface ButtonBaseProps {
  forwardedAs?: 'button';
  href?: never;
  target?: never;
  to?: never;
  type?: 'submit';
  skipRedirectWithTelemetry?: boolean;
}
interface AnchorBaseProps {
  forwardedAs: 'a';
  href: string;
  target?: string;
  to?: never;
  type?: 'submit';
  skipRedirectWithTelemetry?: boolean;
}
interface GatsbyLinkBaseProps {
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  forwardedAs: React.ComponentType<any>;
  href?: never;
  target?: never;
  to: string;
}

interface CTAAbstractButtonProps {
  id: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  telemetryLabel?: string;
}

interface CTADefaultButtonProps extends CTAAbstractButtonProps {
  variant?: ButtonVariant;
  type?: 'submit';
  skipRedirectWithTelemetry?: boolean;
}

type GatsbyLinkButtonProps = GatsbyLinkBaseProps & CTADefaultButtonProps;
export type AnchorButtonProps = AnchorBaseProps & CTADefaultButtonProps;
type ButtonProps = ButtonBaseProps & CTADefaultButtonProps;

export type AllButtonProps = AnchorButtonProps | GatsbyLinkButtonProps | ButtonProps;

const CTAButtonBase = React.forwardRef(
  (
    {
      className,
      onClick,
      as,
      target,
      type,
      telemetryLabel,
      skipRedirectWithTelemetry = false,
      ...restProps
    }: PropsWithChildren<AllButtonProps & { as?: AllButtonProps['forwardedAs'] }>,
    ref
  ) => {
    let rel;
    if (as === 'a') {
      if (isExternalUrl(restProps.href)) {
        target = target || '_blank';
        if (target === '_blank') {
          rel = 'noopener noreferrer';
        }
      }
    }
    if (restProps.to) {
      restProps.to = getUrlWithTrailingSlash(restProps.to);
    }
    const handleOnClick = (event: React.MouseEvent) => {
      if (onClick) {
        onClick(event);
      }

      // Check if user clicked a button going to create wedding, and send info to ImpactRadius.
      if (restProps.href && restProps.href.indexOf('createwedding') > -1) {
        const now = new Date();
        const joyWsid = segment.getCookieByName('joyWsid');
        const joyIrClickId = segment.getCookieByName('joy_irclickid');

        // Only send impact radius tracking event if joyIrClickId cookie is present.
        if (!joyIrClickId && !skipRedirectWithTelemetry) {
          return segment.redirectWithTelemetry(event, undefined, telemetryLabel);
        }

        return trackImpactRadius({
          actionTrackerId: 24456,
          campaignId: 13168,
          clickId: joyIrClickId,
          customerEmail: '',
          customerId: '',
          customerStatus: 'NEW',
          eventDate: now.toISOString(),
          orderId: joyWsid,
        }).finally(() => {
          if (!skipRedirectWithTelemetry) {
            segment.redirectWithTelemetry(event, undefined, telemetryLabel);
          }
        });
      } else if (!skipRedirectWithTelemetry) {
        segment.redirectWithTelemetry(event, undefined, telemetryLabel);
      }
    };
    return <StyledButton ref={ref} as={as} className={className} rel={rel} target={target} {...restProps} onClick={handleOnClick} />;
  }
);

const Button = styled(CTAButtonBase)<PropsWithChildren<AllButtonProps & { as?: never }>>``;

Button.defaultProps = {
  variant: 'primary',
  forwardedAs: 'button',
};

Button.displayName = 'Button';

export { Button };
